/**
 * Schema.jsx — Phase SEO Q1-Q5/Q11 (Feb 2026)
 *
 * Centrale, herbruikbare JSON-LD schema-architectuur voor 5999cargo.nl.
 * Eén component <Schema type="..." data={...} /> die JSON-LD in de <head>
 * injecteert via react-helmet-async (al gebruikt in <SEO />).
 *
 * Geen one-off code. Geen hardcoded content. Branding-data wordt opgehaald
 * uit het centrale BrandingContext + COMPANY (single source of truth).
 *
 * Ondersteunde schema-typen (Q1-Q5):
 *   - "Organization"     → Org-context (logo, sameAs, contactPoint)
 *   - "WebSite"          → SearchAction
 *   - "LocalBusiness"    → Adres, geo, openingHours, areaServed
 *   - "BreadcrumbList"   → Navigatie-trail
 *   - "FAQPage"          → Q&A items
 *   - "Service"          → Per dienst-pagina
 *   - "WebPage"          → Generieke page-schema
 *   - "BlogPosting"      → Per blog artikel
 *
 * Plaatsing:
 *   - Site-breed schemas (Organization + WebSite + LocalBusiness) →
 *     <SiteSchema /> in App.js direct na <BrandingProvider>
 *   - Per-page schemas (FAQPage, Service, BreadcrumbList) →
 *     <Schema type="..." data={...} /> in de page-component
 */
import React, { useEffect, useRef } from "react";

// ============================================================
// COMPANY constants (single source of truth)
// Reflecteert /api/website/company-contact zodat schema en website
// nooit uit sync raken. Houd dit minimaal — alleen velden die in
// schema.org of de UI worden gebruikt.
// ============================================================
export const COMPANY = {
  name: "5999Cargo Group Europe",
  legalName: "5999Cargo Group Europe",
  url: "https://www.5999cargo.nl",
  logo: "https://www.5999cargo.nl/favicon.png", // BrandingContext overschrijft dit waar beschikbaar
  email: "info@5999cargo.nl",
  financeEmail: "finance@5999cargo.nl",
  phone: "+31488235999",
  phoneDisplay: "+31 (0) 488 23 5999",
  whatsapp: "+31630352160",
  vatID: "NL866207053B01",
  eori: "NL866207053",
  address: {
    streetAddress: "Steenhouwerstraat 88",
    postalCode: "3194 AG",
    addressLocality: "Rotterdam",
    addressCountry: "NL",
    addressRegion: "Zuid-Holland",
  },
  // Geo niet vastgezet — voeg later toe via admin/branding als gevalideerd.
  // geo: { latitude: 51.8649, longitude: 4.3782 }, // Hoogvliet/Rotterdam approx
  openingHours: [
    { dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "09:00", closes: "17:00" },
  ],
  // Service-coverage gebieden waar 5999Cargo ENROUTE-verzending biedt.
  // Disclaimer-tekst onder elke destination-pagina geldt.
  areaServed: [
    { type: "Country", name: "Curaçao" },
    { type: "Country", name: "Aruba" },
    { type: "Country", name: "Bonaire" },
    { type: "Country", name: "Sint Maarten" },
    { type: "Country", name: "Suriname" },
    { type: "Country", name: "Netherlands" },
  ],
  sameAs: [
    // Vul aan zodra sociale-media-handles bevestigd zijn.
    // "https://www.facebook.com/5999cargo",
    // "https://www.linkedin.com/company/5999cargo",
    // "https://www.instagram.com/5999cargo",
  ],
};

// ============================================================
// Schema builders (pure functions)
// ============================================================

function buildOrganization(brandingLogo) {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${COMPANY.url}/#organization`,
    name: COMPANY.name,
    legalName: COMPANY.legalName,
    url: COMPANY.url,
    logo: brandingLogo || COMPANY.logo,
    email: COMPANY.email,
    telephone: COMPANY.phone,
    vatID: COMPANY.vatID,
    address: {
      "@type": "PostalAddress",
      streetAddress: COMPANY.address.streetAddress,
      postalCode: COMPANY.address.postalCode,
      addressLocality: COMPANY.address.addressLocality,
      addressCountry: COMPANY.address.addressCountry,
      addressRegion: COMPANY.address.addressRegion,
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer service",
        telephone: COMPANY.phone,
        email: COMPANY.email,
        areaServed: ["NL", "CW", "AW", "SX", "SR", "BQ"],
        availableLanguage: ["nl", "en", "pap"],
      },
    ],
    ...(COMPANY.sameAs.length > 0 ? { sameAs: COMPANY.sameAs } : {}),
  };
}

function buildWebSite() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${COMPANY.url}/#website`,
    url: COMPANY.url,
    name: COMPANY.name,
    publisher: { "@id": `${COMPANY.url}/#organization` },
    inLanguage: "nl-NL",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${COMPANY.url}/blog?search={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

function buildLocalBusiness(brandingLogo) {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${COMPANY.url}/#localbusiness`,
    name: COMPANY.name,
    image: brandingLogo || COMPANY.logo,
    url: COMPANY.url,
    telephone: COMPANY.phone,
    email: COMPANY.email,
    priceRange: "€€",
    address: {
      "@type": "PostalAddress",
      streetAddress: COMPANY.address.streetAddress,
      postalCode: COMPANY.address.postalCode,
      addressLocality: COMPANY.address.addressLocality,
      addressCountry: COMPANY.address.addressCountry,
      addressRegion: COMPANY.address.addressRegion,
    },
    ...(COMPANY.geo
      ? {
          geo: {
            "@type": "GeoCoordinates",
            latitude: COMPANY.geo.latitude,
            longitude: COMPANY.geo.longitude,
          },
        }
      : {}),
    openingHoursSpecification: COMPANY.openingHours.map((s) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: s.dayOfWeek.map((d) => `https://schema.org/${d}`),
      opens: s.opens,
      closes: s.closes,
    })),
    areaServed: COMPANY.areaServed.map((a) => ({
      "@type": a.type,
      name: a.name,
    })),
  };
}

function buildBreadcrumb(items) {
  // items = [{ name, url }] in volgorde Home → … → current
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: it.name,
      ...(it.url ? { item: it.url } : {}),
    })),
  };
}

function buildFAQPage(items) {
  // items = [{ question, answer }]
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items
      .filter((it) => it && it.question && it.answer)
      .map((it) => ({
        "@type": "Question",
        name: String(it.question).trim(),
        acceptedAnswer: {
          "@type": "Answer",
          text: String(it.answer).trim(),
        },
      })),
  };
}

function buildService({ name, description, serviceType, areaServed, url, image }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: name,
    description: description,
    serviceType: serviceType || name,
    provider: { "@id": `${COMPANY.url}/#organization` },
    ...(url ? { url } : {}),
    ...(image ? { image } : {}),
    areaServed: (areaServed || COMPANY.areaServed).map((a) => ({
      "@type": a.type,
      name: a.name,
    })),
  };
}

function buildWebPage({ name, description, url }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name,
    ...(description ? { description } : {}),
    ...(url ? { url } : {}),
    isPartOf: { "@id": `${COMPANY.url}/#website` },
    publisher: { "@id": `${COMPANY.url}/#organization` },
  };
}

function buildBlogPosting({ headline, description, datePublished, dateModified, image, url, author }) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline,
    ...(description ? { description } : {}),
    ...(image ? { image } : {}),
    ...(datePublished ? { datePublished } : {}),
    ...(dateModified ? { dateModified } : {}),
    ...(url ? { url, mainEntityOfPage: url } : {}),
    publisher: { "@id": `${COMPANY.url}/#organization` },
    author: author
      ? { "@type": "Person", name: author }
      : { "@type": "Organization", name: COMPANY.name },
  };
}

// SEO Sprint 2 — Product schema builder.
// Safe-by-default: we never emit AggregateRating, never invent reviews,
// and only emit `offers` when we have a price + a Stripe-buyable
// reference (variant- or product-level). availability is derived from
// the existing `stock_status` field.
function _absoluteUrl(u) {
  if (!u) return undefined;
  if (/^https?:/.test(u)) return u;
  return `${COMPANY.url}${u.startsWith("/") ? "" : "/"}${u}`;
}
function _availabilityFromStatus(status) {
  switch ((status || "").toLowerCase()) {
    case "in_stock":     return "https://schema.org/InStock";
    case "out_of_stock": return "https://schema.org/OutOfStock";
    case "backorder":    return "https://schema.org/BackOrder";
    default:             return undefined;
  }
}
function buildProduct({ product, pageUrl }) {
  if (!product || !product.name) return null;
  const images = [];
  if (product.image_url) images.push(_absoluteUrl(product.image_url));
  if (Array.isArray(product.gallery_images)) {
    product.gallery_images.forEach((g) => {
      const u = typeof g === "string" ? g : g?.url;
      const abs = _absoluteUrl(u);
      if (abs && !images.includes(abs)) images.push(abs);
    });
  }

  const out = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    ...(product.description || product.short_description
      ? { description: String(product.description || product.short_description).trim() }
      : {}),
    ...(images.length ? { image: images } : {}),
    ...(product.sku ? { sku: product.sku } : {}),
    brand: { "@type": "Brand", name: COMPANY.name },
    ...(pageUrl ? { url: pageUrl } : {}),
  };

  // Offers — variable product → AggregateOffer over buyable variants;
  // simple/service product → single Offer (only when buyable + has price).
  const variants = Array.isArray(product.variations) ? product.variations : [];
  if (product.product_type === "variable" && variants.length > 0) {
    const buyable = variants.filter(
      (v) => v && v.is_buyable && (v.sale_price || v.regular_price),
    );
    if (buyable.length > 0) {
      const prices = buyable.map((v) =>
        Number(v.sale_price ?? v.regular_price ?? 0),
      ).filter((n) => n > 0);
      if (prices.length > 0) {
        out.offers = {
          "@type": "AggregateOffer",
          priceCurrency: "EUR",
          lowPrice: Math.min(...prices).toFixed(2),
          highPrice: Math.max(...prices).toFixed(2),
          offerCount: buyable.length,
        };
      }
    }
  } else if (product.is_buyable) {
    const price = Number(product.sale_price ?? product.regular_price ?? 0);
    if (price > 0) {
      const offer = {
        "@type": "Offer",
        priceCurrency: "EUR",
        price: price.toFixed(2),
      };
      const av = _availabilityFromStatus(product.stock_status);
      if (av) offer.availability = av;
      if (pageUrl) offer.url = pageUrl;
      out.offers = offer;
    }
  }
  return out;
}

// ============================================================
// Single reusable component
// ============================================================
const BUILDERS = {
  Organization: (data) => buildOrganization(data?.logo),
  WebSite: () => buildWebSite(),
  LocalBusiness: (data) => buildLocalBusiness(data?.logo),
  BreadcrumbList: (data) => buildBreadcrumb(data?.items || []),
  FAQPage: (data) => buildFAQPage(data?.items || []),
  Service: (data) => buildService(data || {}),
  WebPage: (data) => buildWebPage(data || {}),
  BlogPosting: (data) => buildBlogPosting(data || {}),
  Product: (data) => buildProduct(data || {}),
};

export default function Schema({ type, data }) {
  const idRef = useRef(`ld-${type}-${Math.random().toString(36).slice(2, 8)}`);

  useEffect(() => {
    const builder = BUILDERS[type];
    if (!builder) return;
    let json;
    try {
      json = builder(data || {});
    } catch (e) {
      json = null;
    }
    // FAQPage with 0 entities should not be rendered
    if (
      !json ||
      (type === "FAQPage" && (!json.mainEntity || json.mainEntity.length === 0))
    ) {
      const stale = document.getElementById(idRef.current);
      if (stale) stale.remove();
      return;
    }

    const elId = idRef.current;
    let el = document.getElementById(elId);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = elId;
      el.setAttribute("data-schema-type", type);
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(json);

    // Cleanup on unmount so single-page route changes don't leak schemas
    return () => {
      const e2 = document.getElementById(elId);
      if (e2) e2.remove();
    };
  }, [type, data]);

  return null;
}

// Convenience combo for the homepage / app shell.
export function SiteSchema({ brandingLogo }) {
  return (
    <>
      <Schema type="Organization" data={{ logo: brandingLogo }} />
      <Schema type="WebSite" />
      <Schema type="LocalBusiness" data={{ logo: brandingLogo }} />
    </>
  );
}
